import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { findings as findingsApi } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { localizedCategory } from "../lib/i18nContent";

const STATUS = ["Pendiente", "En Proceso", "Cerrado"];

export default function FollowUpPage() {
  const { user, viewAllStores } = useApp();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [storeFilter, setStoreFilter] = useState("all");
  const load = () => findingsApi.list({ view_all: viewAllStores }).then(setItems).catch(console.error);
  useEffect(() => { load(); }, [viewAllStores]);

  const stores = useMemo(() => [...new Set(items.map((f) => f.store_name).filter(Boolean))], [items]);

  const filtered = useMemo(() => {
    if (storeFilter === "all") return items;
    return items.filter((f) => f.store_name === storeFilter);
  }, [items, storeFilter]);

  const update = async (f, patch) => {
    await findingsApi.patch(f.id, patch);
    toast.success(t("findings.updated"));
    load();
  };

  const canEdit = user?.role === "admin" || user?.role === "gerente";

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="follow-up-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="canales-overline">{t("nav.followup")}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-1">{t("followup.title")}</h1>
          <p className="text-stone-600 mt-2 text-sm max-w-xl">
            {t("followup.subtitle")} {!canEdit && t("followup.readonly_role")}
          </p>
        </div>
        <Select value={storeFilter} onValueChange={setStoreFilter}>
          <SelectTrigger className="w-64" data-testid="store-filter">
            <SelectValue placeholder={t("followup.all_stores")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("followup.all_stores")}</SelectItem>
            {stores.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 mt-8">
        {filtered.length === 0 && (
          <Card className="canales-card p-10 text-center text-stone-500">
            {t("findings.no_findings")}
          </Card>
        )}
        {filtered.map((f) => (
          <Card key={f.id} className="canales-card p-5" data-testid={`followup-card-${f.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-stone-500">
                  {f.store_name} · {localizedCategory(f.category, i18n.language)}
                </div>
                <h4 className="font-semibold mt-1">{f.description}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className="canales-overline">{t("followup.responsible")}</label>
                    <Input
                      defaultValue={f.responsible || ""}
                      onBlur={(e) => canEdit && e.target.value !== (f.responsible || "") && update(f, { responsible: e.target.value })}
                      disabled={!canEdit}
                      className="mt-1"
                      data-testid={`fu-responsible-${f.id}`}
                    />
                  </div>
                  <div>
                    <label className="canales-overline">{t("followup.commitment_date")}</label>
                    <Input
                      type="date"
                      defaultValue={f.commitment_date || ""}
                      onChange={(e) => canEdit && update(f, { commitment_date: e.target.value })}
                      disabled={!canEdit}
                      className="mt-1"
                      data-testid={`fu-date-${f.id}`}
                    />
                  </div>
                  <div>
                    <label className="canales-overline">{t("common.status")}</label>
                    <Select
                      value={f.status}
                      onValueChange={(v) => canEdit && update(f, { status: v })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="mt-1" data-testid={`fu-status-${f.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS.map((s) => (
                          <SelectItem key={s} value={s}>{t(`finding_status.${s}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="canales-overline">{t("followup.notes")}</label>
                  <Textarea
                    defaultValue={f.follow_up_notes || ""}
                    onBlur={(e) => canEdit && e.target.value !== (f.follow_up_notes || "") && update(f, { follow_up_notes: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1 min-h-[64px]"
                    data-testid={`fu-notes-${f.id}`}
                    placeholder={t("followup.notes_placeholder")}
                  />
                </div>
              </div>
              <div className="text-right text-xs text-stone-500 shrink-0">
                <div>{t("findings.col_severity")}: <span className="font-semibold text-stone-700">{t(`severity.${f.severity}`, f.severity)}</span></div>
                <div className="mt-1">{(f.created_at || "").slice(0,10)}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
